








































































































































































































import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import Multiselect from "vue-multiselect";

import {
  AllocationDetailStatus,
  AllocationEntity,
  AllocationOrientation,
} from "./allocation-state";

@Component({ components: { Multiselect } })
export default class AllocationWorkspace extends Vue {
  @Prop({ required: true })
  public readonly items!: AllocationEntity[];

  @Prop({ required: true })
  public readonly factories!: AllocationEntity[];

  @Prop({ required: true })
  public readonly itemLabel!: string;

  @Prop({ default: undefined })
  public readonly getSubjectAnnotation?: (subject: AllocationEntity, orientation: AllocationOrientation) => string;

  @Prop({ default: false })
  public readonly editable!: boolean;

  @Prop({ default: undefined })
  public readonly getDetailStatus?: (
    subject: AllocationEntity,
    detail: AllocationEntity,
    orientation: AllocationOrientation
  ) => AllocationDetailStatus;

  @Prop({ default: undefined })
  public readonly canAddDetail?: (
    subject: AllocationEntity,
    detail: AllocationEntity,
    orientation: AllocationOrientation
  ) => boolean;

  @Prop({ default: undefined })
  public readonly isStagedDetail?: (
    subject: AllocationEntity,
    detail: AllocationEntity,
    orientation: AllocationOrientation
  ) => boolean;

  @Prop({ default: undefined })
  public readonly isModifiedDetail?: (
    subject: AllocationEntity,
    detail: AllocationEntity,
    orientation: AllocationOrientation
  ) => boolean;

  private fOrientation: AllocationOrientation = "item";
  private fOrientationPointer = false;
  private fSubjectFilter = "";
  private fDetailFilter = "";
  private fAddCandidate: AllocationEntity | null = null;
  private fSelectedSubjectId = "";

  get orientation(): AllocationOrientation {
    return this.fOrientation;
  }

  set orientation(value: AllocationOrientation) {
    this.fOrientation = value;
  }

  public subjectAnnotation(subject: AllocationEntity): string {
    return this.getSubjectAnnotation ? this.getSubjectAnnotation(subject, this.orientation) : "";
  }

  get subjectFilter(): string {
    return this.fSubjectFilter;
  }

  set subjectFilter(value: string) {
    this.fSubjectFilter = value;
  }

  get detailFilter(): string {
    return this.fDetailFilter;
  }

  set detailFilter(value: string) {
    this.fDetailFilter = value;
  }

  get addCandidate(): AllocationEntity | null {
    return this.fAddCandidate;
  }

  set addCandidate(value: AllocationEntity | null) {
    this.fAddCandidate = value;
  }

  get selectedSubjectId(): string {
    return this.fSelectedSubjectId;
  }

  set selectedSubjectId(value: string) {
    this.fSelectedSubjectId = value;
  }

  get subjects(): AllocationEntity[] {
    return this.orientation === "item" ? this.items : this.factories;
  }

  get details(): AllocationEntity[] {
    return this.orientation === "item" ? this.factories : this.items;
  }

  get filteredSubjects(): AllocationEntity[] {
    return this.filterEntities(this.subjects, this.subjectFilter);
  }

  get visibleDetails(): AllocationEntity[] {
    const subject = this.selectedSubject;
    if (!subject) {
      return [];
    }
    return this.details.filter(
      (detail) =>
        (this.getDetailStatus &&
          this.getDetailStatus(subject, detail, this.orientation) ===
            "enabled") ||
        (this.isStagedDetail &&
          this.isStagedDetail(subject, detail, this.orientation)) ||
        (this.isModifiedDetail &&
          this.isModifiedDetail(subject, detail, this.orientation))
    );
  }

  get filteredDetails(): AllocationEntity[] {
    return this.filterEntities(this.visibleDetails, this.detailFilter);
  }

  get addCandidates(): AllocationEntity[] {
    const subject = this.selectedSubject;
    const canAddDetail = this.canAddDetail;
    if (!this.editable || !subject || !canAddDetail) {
      return [];
    }
    return this.details.filter((detail) =>
      canAddDetail(subject, detail, this.orientation)
    );
  }

  get selectedSubject(): AllocationEntity | undefined {
    return this.subjects.find(
      (subject) => subject.id === this.selectedSubjectId
    );
  }

  get subjectLabel(): string {
    return this.orientation === "item" ? `${this.itemLabel}s` : "Factories";
  }

  get singularSubjectLabel(): string {
    return this.orientation === "item" ? this.itemLabel : "Factory";
  }

  get detailLabel(): string {
    return this.orientation === "item" ? "Factories" : `${this.itemLabel}s`;
  }

  get singularDetailLabel(): string {
    return this.orientation === "item" ? "Factory" : this.itemLabel;
  }

  get detailHeading(): string {
    if (!this.selectedSubject) {
      return this.detailLabel;
    }

    return `${this.detailLabel} for ${this.selectedSubject.name}`;
  }

  public onOrientationPointer(): void {
    this.fOrientationPointer = true;
  }

  public onOrientationKeyboard(): void {
    this.fOrientationPointer = false;
  }

  public changeOrientation(
    orientation: AllocationOrientation,
    event: Event
  ): void {
    if (this.fOrientationPointer && event.target instanceof HTMLElement) {
      event.target.blur();
    }
    this.setOrientation(orientation);
  }

  public setOrientation(orientation: AllocationOrientation): void {
    if (this.orientation === orientation) {
      return;
    }

    this.orientation = orientation;
    this.subjectFilter = "";
    this.detailFilter = "";
    this.addCandidate = null;
    this.selectFirstVisibleSubject();
    this.$emit("orientation", orientation);
  }

  public selectMobileSubject(subject: AllocationEntity | null): void {
    if (subject) {
      this.selectedSubjectId = subject.id;
    }
  }

  public addCounterpart(): void {
    const candidate = this.addCandidates.find(
      (item) => this.addCandidate && item.id === this.addCandidate.id
    );
    if (!this.selectedSubject || !candidate) {
      return;
    }
    this.$emit("add", this.selectedSubject, candidate, this.orientation);
    this.addCandidate = null;
  }

  protected created(): void {
    this.selectFirstVisibleSubject();
  }

  private filterEntities(
    entities: AllocationEntity[],
    filter: string
  ): AllocationEntity[] {
    const normalizedFilter = filter.trim().toLocaleLowerCase();
    if (!normalizedFilter) {
      return entities;
    }

    return entities.filter((entity) =>
      entity.name.toLocaleLowerCase().includes(normalizedFilter)
    );
  }

  private selectFirstVisibleSubject(): void {
    this.selectedSubjectId = this.filteredSubjects.length
      ? this.filteredSubjects[0].id
      : "";
  }

  @Watch("selectedSubjectId")
  private clearAddCandidateSelection(): void {
    this.addCandidate = null;
  }

  @Watch("subjects")
  private reconcileSelectedSubject(subjects: AllocationEntity[]): void {
    if (
      this.selectedSubjectId &&
      !subjects.some((subject) => subject.id === this.selectedSubjectId)
    ) {
      this.selectedSubjectId = "";
    }
  }
}
